/**
 * DataSourceSelectionStep
 *
 * Step 2: User chooses how to import products - via feed upload or website crawl.
 * Part of AEO Onboarding Wizard - Crawl-Based Path
 *
 * Date: February 8, 2026
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, FileSpreadsheet, Globe, ArrowRight, Loader2 } from 'lucide-react';

interface DataSourceSelectionStepProps {
  onSelect: (type: 'feed' | 'crawl') => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

export function DataSourceSelectionStep({
  onSelect,
  isLoading,
  error
}: DataSourceSelectionStepProps) {
  const handleSelect = async (type: 'feed' | 'crawl') => {
    if (isLoading) return;
    await onSelect(type);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Choose How to Import Products</CardTitle>
        <CardDescription>
          Select the method that best fits your situation. You can always add more products later.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Product Feed Option */}
          <Card
            className={`cursor-pointer transition-all hover:border-primary hover:shadow-md ${
              isLoading ? 'opacity-50 pointer-events-none' : ''
            }`}
            onClick={() => handleSelect('feed')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <FileSpreadsheet className="h-10 w-10 text-blue-600" />
                <Badge variant="secondary">Recommended</Badge>
              </div>
              <CardTitle className="text-lg mt-4">Product Feed</CardTitle>
              <CardDescription className="text-sm">
                Upload a CSV, TSV, or XML file with your product catalog
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li className="flex items-center gap-2">
                  <ArrowRight className="h-3 w-3" />
                  Best for established catalogs
                </li>
                <li className="flex items-center gap-2">
                  <ArrowRight className="h-3 w-3" />
                  Supports Google Shopping format
                </li>
                <li className="flex items-center gap-2">
                  <ArrowRight className="h-3 w-3" />
                  Import from URL or file upload
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Website Crawl Option */}
          <Card
            className={`cursor-pointer transition-all hover:border-primary hover:shadow-md ${
              isLoading ? 'opacity-50 pointer-events-none' : ''
            }`}
            onClick={() => handleSelect('crawl')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <Globe className="h-10 w-10 text-green-600" />
                <Badge variant="outline">Quick Start</Badge>
              </div>
              <CardTitle className="text-lg mt-4">Website Crawl</CardTitle>
              <CardDescription className="text-sm">
                Automatically discover products by crawling your website
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li className="flex items-center gap-2">
                  <ArrowRight className="h-3 w-3" />
                  Discovers up to 50 products
                </li>
                <li className="flex items-center gap-2">
                  <ArrowRight className="h-3 w-3" />
                  Uses sitemap when available
                </li>
                <li className="flex items-center gap-2">
                  <ArrowRight className="h-3 w-3" />
                  Great for services & quick setup
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
            <span className="text-muted-foreground">Processing selection...</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
